<template>
    <form
        :id="formId"
        dusk="AddOnEditor"
        class="flex flex-col gap-4 text-theme-text"
        novalidate
        @submit.prevent="submit"
    >
        <p
            v-if="editor.saveError"
            ref="saveError"
            dusk="AddOnEditorSaveError"
            role="alert"
            class="m-0 break-words rounded-lg bg-theme-danger-bg px-3 py-2 text-[12.5px] text-theme-danger"
        >
            {{ saveFailure }}
        </p>

        <AddOnAnchorBlock :anchor="draft.anchor" />

        <div>
            <div class="mb-1 flex items-baseline justify-between">
                <label :for="ids.title" class="text-[12px] font-medium text-theme-toolbar-icon">{{
                    strings.titleLabel
                }}</label>
                <span
                    v-if="editor.titleError"
                    :id="ids.titleError"
                    dusk="AddOnEditorTitleError"
                    class="text-[11px] text-theme-danger"
                    role="alert"
                >
                    {{ strings.titleRequired }}
                </span>
            </div>
            <div class="flex items-start gap-2">
                <input
                    :id="ids.title"
                    ref="title"
                    dusk="AddOnEditorTitle"
                    type="text"
                    :value="draft.title"
                    :placeholder="strings.titlePlaceholder"
                    aria-required="true"
                    :maxlength="titleMaxLength"
                    :aria-invalid="String(editor.titleError)"
                    :aria-describedby="editor.titleError ? ids.titleError : null"
                    :class="[inputClasses, editor.titleError ? 'border-theme-danger' : 'border-theme-border']"
                    @input="setField('title', $event.target.value)"
                />
                <AddOnIconPicker
                    :type="addOnEditorType"
                    :type-auto="draft.typeAuto"
                    :inferred-type="addOnEditorInferredType"
                    @pick="setAddOnEditorType"
                />
            </div>
        </div>

        <div>
            <!-- Text, not a label: the body is written in a contenteditable, which
                 no label can point at. The field carries its own name in aria-label. -->
            <span dusk="AddOnEditorBodyLabel" class="mb-1 block text-[12px] font-medium text-theme-toolbar-icon">{{
                strings.content
            }}</span>
            <RichTextInput
                dusk="AddOnEditorBody"
                profile="addon"
                rich
                :value="draft.body"
                :aria-label="strings.content"
                :placeholder="strings.bodyPlaceholder"
                @input="setField('body', $event)"
            />
        </div>

        <details
            :open="detailsOpen"
            class="rounded-lg border border-theme-border"
            @toggle="detailsOpen = $event.target.open"
        >
            <!-- A flex summary loses its disclosure triangle, and a list item cannot centre
                 its line in a taller box, so the padding grows with the height. -->
            <summary
                dusk="AddOnEditorMoreDetails"
                class="min-h-11 cursor-pointer select-none px-3 py-3 text-[12.5px] font-medium text-theme-text focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent md:min-h-0 md:py-2"
            >
                {{ strings.moreDetails }}
                <span class="ml-1 text-[11px] font-normal text-theme-toolbar-icon">{{ strings.moreDetailsHint }}</span>
            </summary>
            <div class="flex flex-col gap-3 px-3 pb-3">
                <template v-for="field in detailFields">
                    <label :key="field.key" class="block text-[12px] font-medium text-theme-toolbar-icon">
                        {{ strings[field.key] }}
                        <input
                            :dusk="`AddOnEditor-${field.key}`"
                            :type="field.type"
                            :value="draft[field.key]"
                            :placeholder="strings[`${field.key}Placeholder`]"
                            :maxlength="field.maxLength"
                            :class="[inputClasses, 'mt-1 border-theme-border font-normal text-theme-text']"
                            @input="setField(field.key, $event.target.value)"
                        />
                    </label>
                    <!-- A file is the other way to name the image, so it sits under the URL. -->
                    <div v-if="field.key === 'imageUrl'" :key="`${field.key}-file`">
                        <label class="block text-[12px] font-medium text-theme-toolbar-icon">
                            {{ strings.imageFile }}
                            <input
                                ref="imageFile"
                                dusk="AddOnEditor-imageFile"
                                type="file"
                                :accept="imageFileTypes"
                                :aria-invalid="String(editor.imageError)"
                                :aria-describedby="editor.imageError ? ids.imageError : null"
                                :class="fileInputClasses"
                                @change="chooseImageFile"
                            />
                        </label>
                        <p
                            v-if="editor.imageError"
                            :id="ids.imageError"
                            ref="imageError"
                            dusk="AddOnEditorImageError"
                            role="alert"
                            class="m-0 mt-1 text-[11px] text-theme-danger"
                        >
                            {{ strings.imageUnreadable }}
                        </p>
                    </div>
                </template>
                <img
                    v-if="imagePreviewSrc"
                    dusk="AddOnEditorImagePreview"
                    :src="imagePreviewSrc"
                    :alt="draft.imageCaption || ''"
                    class="w-full rounded"
                />
            </div>
        </details>
    </form>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import AddOnAnchorBlock from './AddOnAnchorBlock.vue';
import AddOnIconPicker from './AddOnIconPicker.vue';
import RichTextInput from '../Components/RichTextInput.vue';
import { IMAGE_FILE_TYPES } from './addOnImage';
import { addOnStrings } from './addOnStrings.js';

const INPUT =
    'w-full min-h-11 md:min-h-0 rounded-lg border bg-theme-bg px-3 py-2 text-sm text-theme-text placeholder-theme-toolbar-icon outline-none focus-visible:ring-2 focus-visible:ring-theme-accent';

// The lengths the host keeps (Farfalla's add-on validation), capped while
// typing so a save never comes back refused over them.
const TITLE_MAX_LENGTH = 255;

const IMAGE_FILE_ACCEPT = IMAGE_FILE_TYPES.join(',');

const FILE_INPUT =
    'mt-1 block w-full rounded-lg text-[12px] font-normal text-theme-text focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent file:mr-3 file:min-h-11 md:file:min-h-0 file:cursor-pointer file:rounded-lg file:border file:border-solid file:border-theme-button-border file:bg-theme-button-bg file:px-3 file:py-2 file:text-[12.5px] file:font-medium file:text-theme-text';

// The order the viewer renders them in, so nothing the reader sees is unreachable.
const DETAIL_FIELDS = [
    { key: 'pretitle', type: 'text', maxLength: 255 },
    { key: 'author', type: 'text', maxLength: 255 },
    { key: 'byLine', type: 'text', maxLength: 1000 },
    { key: 'imageUrl', type: 'url' },
    { key: 'imageCaption', type: 'text', maxLength: 2000 },
];

export default {
    name: 'AddOnEditor',
    components: { AddOnAnchorBlock, AddOnIconPicker, RichTextInput },
    props: {
        /** Ties the panel's Save button, which renders in the footer slot, to this form. */
        formId: { type: String, required: true },
    },
    data() {
        return {
            detailsOpen: false,
            imageFileUrl: null,
        };
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters([
            'addOnEditorInferredType',
            'addOnEditorType',
            'onlyMobileVisible',
            'shouldAddOnEditorPublish',
            'ownsAddOnForm',
        ]),
        ...mapGetters({ editor: 'addOnBeingEdited' }),
        ids() {
            return {
                title: `add-on-title-${this._uid}`,
                titleError: `add-on-title-error-${this._uid}`,
                imageError: `add-on-image-error-${this._uid}`,
            };
        },
        strings() {
            return addOnStrings(this.localizations, this.lang);
        },
        draft() {
            return this.editor.draft;
        },
        // The host says which field it refused; any other failure has no words of its own.
        saveFailure() {
            return typeof this.editor.saveError === 'string' ? this.editor.saveError : this.strings.saveFailed;
        },
        inputClasses() {
            return INPUT;
        },
        fileInputClasses() {
            return FILE_INPUT;
        },
        imageFileTypes() {
            return IMAGE_FILE_ACCEPT;
        },
        detailFields() {
            return DETAIL_FIELDS;
        },
        titleMaxLength() {
            return TITLE_MAX_LENGTH;
        },
        imagePreviewSrc() {
            return this.imageFileUrl || this.draft.imageUrl;
        },
        // A piece opened to be read on a phone must not be met by the keyboard,
        // the rule the note editor follows. A new one is opened to be written.
        shouldFocusTitleOnMount() {
            return !(this.onlyMobileVisible && this.editor.id != null);
        },
        hasDetails() {
            return DETAIL_FIELDS.some(field => this.draft[field.key]);
        },
    },
    watch: {
        // Metadata stays folded unless this piece already uses it.
        editor: {
            immediate: true,
            handler() {
                this.detailsOpen = this.hasDetails;
            },
        },
        // The button that failed sits in the panel's footer, so on a form long
        // enough to scroll this line can appear where nobody is looking.
        'editor.saveError'(failed) {
            if (failed) {
                this.$nextTick(() => this.$refs.saveError?.scrollIntoView?.({ block: 'nearest' }));
            }
        },
        // Whatever replaces or drops the file (another pick, a typed URL, another form) releases its object URL.
        'draft.imageFile': {
            immediate: true,
            handler(file, previous) {
                // Vue calls again with the same File whenever the store copies the draft.
                if (file === previous) return;

                this.showImageFile(file);
            },
        },
        // The field sits in the folded details, and the Save that failed in the panel's footer.
        'editor.imageError'(failed) {
            if (!failed) return;

            this.detailsOpen = true;
            // Refs inside a v-for come as lists, and this one renders once.
            this.$nextTick(() => this.$refs.imageError?.[0]?.scrollIntoView?.({ block: 'nearest' }));
        },
        // Every change reaches the store as it is made. The store saves a
        // draft a second after its last change and leaves any other piece to its Save.
        draft: {
            deep: true,
            handler() {
                this.autosaveAddOnEditor();
            },
        },
    },
    mounted() {
        if (this.shouldFocusTitleOnMount) {
            this.focusTitle();
        }
    },
    beforeDestroy() {
        this.showImageFile(null);
    },
    methods: {
        ...mapActions(['saveAddOnEditor', 'autosaveAddOnEditor']),
        ...mapMutations(['setAddOnEditorField', 'setAddOnEditorType']),
        // Enter in the title submits too, so publishing is held here rather than on
        // the footer's button, for the panel to ask first. That button is the
        // panel's, so the panel hears of a save the host refused. A file that could
        // not be read points at the form's own picker.
        async submit() {
            if (this.shouldAddOnEditorPublish) {
                this.$emit('request-publish');
                return;
            }

            const editor = this.editor;
            if (await this.saveAddOnEditor()) return;
            // A form opened while the save travelled did not ask for it.
            if (!this.ownsAddOnForm(editor)) return;

            if (editor.imageError) this.focusImageFile();
            else if (editor.saveError) this.$emit('save-failed');
        },
        setField(key, value) {
            this.setAddOnEditorField({ key, value });
        },
        chooseImageFile(event) {
            this.setField('imageFile', event.target.files[0] ?? null);
        },
        focusTitle() {
            this.$refs.title?.focus();
        },
        // The picker sits in the folded details, which have to open before it can take the focus.
        focusImageFile() {
            this.detailsOpen = true;
            this.$nextTick(() => this.$refs.imageFile?.[0]?.focus());
        },
        /**
         * Shows the file the draft holds through an object URL of its own,
         * released as soon as the file goes. The panel's preview shows the
         * same URL.
         */
        showImageFile(file) {
            if (this.imageFileUrl) URL.revokeObjectURL(this.imageFileUrl);
            this.imageFileUrl = file ? URL.createObjectURL(file) : null;
            // A file dropped for a typed URL would still show its name in the picker.
            const picker = this.$refs.imageFile?.[0];
            if (!file && picker) picker.value = '';
            this.$emit('image-preview', this.imageFileUrl);
        },
    },
};
</script>
